import { useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";

export interface TabItem {
  id: string;
  label: string;
  badge?: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  className?: string;
}

/**
 * Accessible tab strip with a sliding volt underline (shared layout
 * animation). Implements the WAI-ARIA tabs keyboard pattern: ←/→ move focus,
 * Home/End jump to ends. The active indicator collapses under reduced motion.
 */
export function Tabs({ items, value, defaultValue, onChange, className }: TabsProps) {
  const { t } = useTranslation();
  const groupId = useId();
  const reduce = useReducedMotion();
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.id);
  const active = value ?? internal;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (id: string) => {
    if (value === undefined) setInternal(id);
    onChange?.(id);
  };

  const onKeyDown = (e: KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % items.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    else return;
    e.preventDefault();
    refs.current[next]?.focus();
    select(items[next].id);
  };

  return (
    <div
      role="tablist"
      aria-label={t("tabs.sectionsAria")}
      className={cn(
        "inline-flex items-center gap-1 rounded-pill bg-surface-sunken p-1.5 ring-1 ring-line",
        className,
      )}
    >
      {items.map((item, i) => {
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            ref={(el) => (refs.current[i] = el)}
            role="tab"
            id={`${groupId}-tab-${item.id}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => select(item.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              "relative inline-flex items-center gap-2 rounded-pill px-4 py-2 font-display text-sm font-bold transition-colors duration-fast",
              isActive ? "text-white" : "text-body-soft hover:text-body",
            )}
          >
            {isActive && (
              <motion.span
                layoutId={`${groupId}-pill`}
                transition={
                  reduce ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 36 }
                }
                className="absolute inset-0 -z-10 rounded-pill bg-grad-coral shadow-volt"
              />
            )}
            <span className="relative z-10">{item.label}</span>
            {item.badge && <span className="relative z-10">{item.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}
